import React, { useState, useEffect, useRef } from "react";
import { FaTimes, FaSave, FaGripVertical } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import dataTransferImage from "../assets/data-transfer.png";
import ConfiguracoesProjeto from "./ConfiguracoesProjeto";
import "./EntityDetails.css";

const INCC_API_URL = "https://incc-api.onrender.com";
const FOTO_BUCKET = "projects_photos";
const SETOR_FOTO_BUCKET = "setores_photos";

// ── Lista de etapas do projeto ──
const ETAPAS_PROJETO = [
  "Planejamento",
  "Fundação",
  "Estrutura",
  "Alvenaria",
  "Instalações",
  "Acabamento",
  "Entrega",
  "Finalizado",
];

// ── NOVO: estilo padrão dos inputs de texto ──
const INPUT_STYLE = { width: "100%", padding: "8px", boxSizing: "border-box" };

const formatarMoeda = (valor) => {
  if (valor == null || valor === "") return "—";
  const num = Number(valor);
  return isNaN(num) ? "—" : num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

// ── Máscara de CNPJ ──
const mascararCnpj = (v) => {
  const d = String(v || "").replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const extrairTexto = (item) => {
  if (!item) return "";
  return typeof item === "string" ? item : (item.name ?? item.nome ?? "");
};

const parseMesAno = (mesAno) => {
  if (!mesAno) return null;
  const MESES = ["janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];
  const [mes, ano] = mesAno.split("/");
  const idx = MESES.findIndex((m) => m.toLowerCase() === mes.toLowerCase());
  return idx === -1 || !ano ? null : new Date(parseInt(ano), idx, 1);
};

const encontrarIndiceParaData = (historico, data) => {
  if (!historico?.length || !data) return null;
  const target = new Date(data);
  let melhor = null, menorDiff = Infinity;
  for (const row of historico) {
    const d = row.data ? new Date(row.data) : parseMesAno(row.mes);
    if (!d || isNaN(d.getTime())) continue;
    const diff = Math.abs(d - target);
    if (diff < menorDiff) { menorDiff = diff; melhor = row; }
  }
  return melhor;
};

async function fetchMembrosDoProject(projectId) {
  if (!projectId) return [];
  const { data: rows, error } = await supabase.from("project_members").select("user_id").eq("project_id", projectId);
  if (error || !rows?.length) return [];
  const ids = rows.map((r) => r.user_id).filter(Boolean);
  if (!ids.length) return [];
  const { data: perfis } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", ids);
  return perfis || [];
}

async function saveMembros(projectId, membros, addedBy) {
  if (!projectId) return;
  await supabase.from("project_members").delete().eq("project_id", projectId);
  if (!membros.length) return;
  const inserts = membros.map((m) => ({ project_id: projectId, user_id: m.id, added_by: addedBy || m.id }));
  await supabase.from("project_members").insert(inserts).select();
}

async function saveMembrosSetor(setorId, membros, addedBy) {
  if (!setorId) return;
  await supabase.from("setor_members").delete().eq("setor_id", setorId);
  if (!membros.length) return;
  const inserts = membros.map((m) => ({ setor_id: setorId, user_id: m.id, added_by: addedBy || m.id }));
  await supabase.from("setor_members").insert(inserts).select();
}

async function uploadFotoProjeto(projectId, file) {
  const ext = file.name.split(".").pop().toLowerCase();
  const path = `${projectId}.${ext}`;
  await supabase.storage.from(FOTO_BUCKET).upload(path, file, { upsert: true, contentType: file.type || `image/${ext}`, cacheControl: "3600" });
  const { data: urlData } = supabase.storage.from(FOTO_BUCKET).getPublicUrl(path);
  return `${urlData.publicUrl}?v=${Date.now()}`;
}

async function removerFotoProjeto(photoUrl) {
  try {
    const match = photoUrl.match(new RegExp(`${FOTO_BUCKET}/(.+?)(?:\\?|$)`));
    const path = match?.[1];
    if (path) await supabase.storage.from(FOTO_BUCKET).remove([path]);
  } catch (e) {}
}

async function removerFotosSetor(setorId) {
  try {
    const { data: oldPhotos } = await supabase.from("setores_photos").select("photo_url").eq("setor_id", setorId);
    if (oldPhotos?.length) {
      const fileNames = oldPhotos.map((p) => p.photo_url.split("/").pop());
      await supabase.storage.from(SETOR_FOTO_BUCKET).remove(fileNames);
      await supabase.from("setores_photos").delete().eq("setor_id", setorId);
    }
  } catch (e) {}
}

async function uploadFotoSetor(setorId, file) {
  await removerFotosSetor(setorId);
  const fileName = `setores/${setorId}_${Date.now()}_${file.name}`;
  const { error: uploadError } = await supabase.storage.from(SETOR_FOTO_BUCKET).upload(fileName, file);
  if (uploadError) throw uploadError;
  const { data } = supabase.storage.from(SETOR_FOTO_BUCKET).getPublicUrl(fileName);
  const publicUrl = data?.publicUrl;
  if (!publicUrl) throw new Error("Falha ao gerar URL pública da imagem do setor.");
  await supabase.from("setores_photos").insert({ setor_id: setorId, photo_url: publicUrl });
  return publicUrl;
}

function hashColor(str) {
  if (!str) return { bg: "#4a6fa5", fg: "#ffffff" };
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  const hue = Math.abs(h) % 360;
  return { bg: `hsl(${hue},55%,42%)`, fg: "#ffffff" };
}

function useDragSort(setItems) {
  const dragIndex = useRef(null);
  return {
    handleDragStart: (e, i) => { dragIndex.current = i; e.dataTransfer.effectAllowed = "move"; },
    handleDragOver: (e) => e.preventDefault(),
    handleDrop: (e, i) => {
      e.preventDefault();
      const from = dragIndex.current, to = i;
      if (from === null || from === to) return;
      setItems((prev) => { const next = [...prev]; const [m] = next.splice(from, 1); next.splice(to, 0, m); return next; });
      dragIndex.current = null;
    },
    handleDragEnd: () => { dragIndex.current = null; },
  };
}

function ImportarPopover({ containerId, currentProjectId, anchorRef, onImport, onClose }) {
  const popoverRef = useRef(null);
  const [projetos, setProjetos] = useState([]);
  const [loadingProj, setLoadingProj] = useState(true);
  const [projetoSel, setProjetoSel] = useState(null);
  const [detalhes, setDetalhes] = useState(null);
  const [loadingDet, setLoadingDet] = useState(false);
  const [selPav, setSelPav] = useState([]);
  const [selEap, setSelEap] = useState([]);
  const [step, setStep] = useState("projetos");
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorRef?.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + window.scrollY + 8, left: Math.max(8, r.right + window.scrollX - 300) });
    }
  }, [anchorRef]);

  useEffect(() => {
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target) && anchorRef?.current && !anchorRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  useEffect(() => {
    (async () => {
      setLoadingProj(true);
      try {
        let uid = containerId;
        if (!uid && currentProjectId) {
          const { data: p } = await supabase.from("projects").select("user_id").eq("id", currentProjectId).single();
          uid = p?.user_id;
        }
        if (!uid) return;
        const { data } = await supabase.from("projects").select("id, name").eq("user_id", uid).neq("id", currentProjectId).order("name");
        if (data) setProjetos(data);
      } finally { setLoadingProj(false); }
    })();
  }, [containerId, currentProjectId]);

  const selecionarProjeto = async (proj) => {
    setProjetoSel(proj); setStep("itens"); setLoadingDet(true); setSelPav([]); setSelEap([]);
    try {
      const [{ data: pavs }, { data: eaps }] = await Promise.all([
        supabase.from("pavimentos").select("id, name, ordem").eq("project_id", proj.id).order("ordem"),
        supabase.from("eap").select("id, name, ordem, orcamento_base, bloco, classificacao").eq("project_id", proj.id).order("ordem"),
      ]);
      setDetalhes({ pavimentos: pavs || [], eap: eaps || [] });
    } catch { setDetalhes({ pavimentos: [], eap: [] }); }
    finally { setLoadingDet(false); }
  };

  const togglePav = (id) => setSelPav((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const toggleEap = (id) => setSelEap((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const hasPavs = (detalhes?.pavimentos ?? []).length > 0;
  const hasEaps = (detalhes?.eap ?? []).length > 0;
  const totalSel = selPav.length + selEap.length;

  const handleImport = () => {
    const pav = (detalhes?.pavimentos ?? []).filter((p) => selPav.includes(p.id)).map((p) => ({ id: null, nome: p.name, _tempId: Math.random() }));
    const eap = (detalhes?.eap ?? []).filter((e) => selEap.includes(e.id)).map((e) => ({ id: null, nome: e.name, orcamento_base: e.orcamento_base ?? null, _tempId: Math.random() }));
    onImport({ pavimentos: pav, eap });
    onClose();
  };

  return (
    <div ref={popoverRef} className="imp-popover" style={{ top: pos.top, left: pos.left }}>
      <div className="imp-header">
        {step === "itens" && <button type="button" className="imp-nav-btn" onClick={() => { setStep("projetos"); setProjetoSel(null); setDetalhes(null); }}>‹</button>}
        <span className="imp-title">{step === "projetos" ? "Importar de outro projeto" : projetoSel?.name}</span>
        <button type="button" className="imp-nav-btn" onClick={onClose}>×</button>
      </div>
      <div className="imp-body">
        {step === "projetos" && (
          <>
            <p className="imp-hint">Escolha um projeto para copiar seus pavimentos e/ou EAP.</p>
            {loadingProj ? <div className="imp-loading">Carregando…</div>
              : projetos.length === 0 ? <div className="imp-empty">Nenhum outro projeto encontrado.</div>
              : <ul className="imp-proj-list">{projetos.map((p) => (
                  <li key={p.id} className="imp-proj-item" onClick={() => selecionarProjeto(p)}>
                    <span className="imp-proj-avatar">{p.name?.charAt(0).toUpperCase()}</span>
                    <span className="imp-proj-name">{p.name}</span>
                    <span className="imp-proj-arrow">›</span>
                  </li>
                ))}</ul>}
          </>
        )}
        {step === "itens" && (
          loadingDet ? <div className="imp-loading">Carregando…</div>
          : !hasPavs && !hasEaps ? <div className="imp-empty">Sem pavimentos ou EAP.</div>
          : <>
              {hasPavs && <div className="imp-group">
                <div className="imp-group-header"><span className="imp-group-title">Pavimentos</span><div className="imp-group-actions"><button type="button" className="imp-sel-btn" onClick={() => setSelPav(detalhes.pavimentos.map((p) => p.id))}>Todos</button><button type="button" className="imp-sel-btn imp-sel-btn--ghost" onClick={() => setSelPav([])}>Nenhum</button></div></div>
                <ul className="imp-item-list">{detalhes.pavimentos.map((p) => (<li key={p.id} className={`imp-item ${selPav.includes(p.id) ? "imp-item--sel" : ""}`} onClick={() => togglePav(p.id)}><span className="imp-checkbox">{selPav.includes(p.id) ? "✓" : ""}</span><span className="imp-item-name">{p.name}</span></li>))}</ul>
              </div>}
              {hasEaps && <div className="imp-group">
                <div className="imp-group-header"><span className="imp-group-title">EAP</span><div className="imp-group-actions"><button type="button" className="imp-sel-btn" onClick={() => setSelEap(detalhes.eap.map((e) => e.id))}>Todos</button><button type="button" className="imp-sel-btn imp-sel-btn--ghost" onClick={() => setSelEap([])}>Nenhum</button></div></div>
                <ul className="imp-item-list">{detalhes.eap.map((e) => (<li key={e.id} className={`imp-item ${selEap.includes(e.id) ? "imp-item--sel" : ""}`} onClick={() => toggleEap(e.id)}><span className="imp-checkbox">{selEap.includes(e.id) ? "✓" : ""}</span><span className="imp-item-name">{e.name}</span></li>))}</ul>
              </div>}
            </>
        )}
      </div>
      {step === "itens" && !loadingDet && (hasPavs || hasEaps) && (
        <div className="imp-footer">
          <span className="imp-sel-count">{totalSel === 0 ? "Nenhum selecionado" : `${totalSel} selecionado${totalSel > 1 ? "s" : ""}`}</span>
          <button type="button" className="imp-btn-import" disabled={totalSel === 0} onClick={handleImport}>Importar</button>
        </div>
      )}
    </div>
  );
}

export default function EditarEntity({ entity, eap, onClose, onSave, inccHistorico, containerId, entityType = "project" }) {
  const isSetor = entityType === "setor";
  const tabsDisponiveis = isSetor ? ["principal", "integrantes"] : ["principal", "integrantes", "pavimentos", "eap", "configuracoes"];

  const [activeTab, setActiveTab] = useState("principal");
  const [form, setForm] = useState({ 
    nome: entity.name ?? "",
    data_inicio: entity.data_inicio ?? "", 
    data_finalizacao: entity.data_finalizacao ?? "", 
    engenheiro_id: entity.engenheiro_id ?? "",
    etapa_atual: entity.etapa_atual ?? "",
  });

  // ── Dados da empresa (razão social + CNPJ) ──
  const [dadosEmpresa, setDadosEmpresa] = useState({
    razao_social: entity.razao_social ?? "",
    cnpj: mascararCnpj(entity.cnpj ?? ""),
  });

  const [membrosSelecionados, setMembrosSelecionados] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [eapValores, setEapValores] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [usuarioLogadoId, setUsuarioLogadoId] = useState(null);
  const [showImportPopover, setShowImportPopover] = useState(false);
  const importBtnRef = useRef(null);
  const [fotoPreview, setFotoPreview] = useState(entity.photo_url ?? null);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoRemovida, setFotoRemovida] = useState(false);
  const fotoInputRef = useRef(null);
  const [cropSrc, setCropSrc] = useState(null);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [cropScale, setCropScale] = useState(1);
  const cropImgRef = useRef(null);
  const CROP_SIZE = 400;

  // ── Estado de Endereço (CEP) ──
  const [endereco, setEndereco] = useState({
    cep: entity.cep ?? "",
    endereco: entity.endereco ?? "",
    numero: entity.numero ?? "",
    complemento: entity.complemento ?? "",
    bairro: entity.bairro ?? "",
    cidade: entity.cidade ?? "",
    uf: entity.uf ?? "",
  });
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [erroCep, setErroCep] = useState("");

  // ── Busca por CEP (ViaCEP) + máscara ──
  const buscarCep = async (cepLimpo) => {
    if (cepLimpo.length !== 8) return;
    setBuscandoCep(true);
    setErroCep("");
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await resp.json();
      if (data.erro) { setErroCep("CEP não encontrado."); return; }
      setEndereco((prev) => ({
        ...prev,
        endereco: data.logradouro || prev.endereco,
        bairro: data.bairro || prev.bairro,
        cidade: data.localidade || prev.cidade,
        uf: data.uf || prev.uf,
      }));
    } catch {
      setErroCep("Erro ao buscar CEP. Tente novamente.");
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleCepChange = (e) => {
    let v = e.target.value.replace(/\D/g, "").slice(0, 8);
    const formatado = v.length > 5 ? `${v.slice(0, 5)}-${v.slice(5)}` : v;
    setEndereco((prev) => ({ ...prev, cep: formatado }));
    if (v.length === 8) buscarCep(v);
  };

  // ── Geocoding automático (endereço → lat/lon) na hora de salvar ──
  const geocodificarEndereco = async (end) => {
    if (!end.endereco || !end.cidade || !end.uf) return { latitude: null, longitude: null };
    const query = `${end.endereco}, ${end.numero || ""}, ${end.cidade}, ${end.uf}, Brasil`;
    try {
      const resp = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=br&q=${encodeURIComponent(query)}`
      );
      const results = await resp.json();
      if (results?.[0]) {
        return { latitude: parseFloat(results[0].lat), longitude: parseFloat(results[0].lon) };
      }
    } catch (e) {
      console.warn("Erro ao geocodificar endereço:", e);
    }
    return { latitude: null, longitude: null };
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user?.id) setUsuarioLogadoId(data.user.id);
    });
  }, []);

  useEffect(() => {
    (async () => {
      const cid = containerId || entity?.container_id || entity?.user_id; if (!cid) return;
      const idsSet = new Set([cid]);
      const { data: convites } = await supabase.from("convites").select("user_id").eq("container_id", cid).eq("status", "aceito");
      (convites || []).forEach((c) => { if (c.user_id) idsSet.add(c.user_id); });
      const { data: perfis } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", [...idsSet]);
      if (perfis) setProfiles(perfis);
    })();
  }, [containerId, entity?.container_id]);

  const isDono = entity?.owner_id === usuarioLogadoId || entity?.user_id === usuarioLogadoId;

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (fotoInputRef.current) fotoInputRef.current.value = "";
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { const scale = Math.max(CROP_SIZE / img.width, CROP_SIZE / img.height); setCropScale(scale); setCropOffset({ x: (CROP_SIZE - img.width * scale) / 2, y: (CROP_SIZE - img.height * scale) / 2 }); cropImgRef.current = img; setCropSrc(url); };
    img.src = url;
  };

  const handleRemoverFoto = () => { setFotoFile(null); setFotoRemovida(true); setFotoPreview(null); setCropSrc(null); if (fotoInputRef.current) fotoInputRef.current.value = ""; };

  const handleCropMouseDown = (e) => {
    e.preventDefault();
    const start = { mx: e.clientX, my: e.clientY, ox: cropOffset.x, oy: cropOffset.y };
    const clamp = (nx, ny, sc) => { const img = cropImgRef.current; if (!img) return { x: nx, y: ny }; const iw = img.width * sc, ih = img.height * sc; return { x: Math.max(Math.min(0, CROP_SIZE - iw), Math.min(Math.max(0, CROP_SIZE - iw), nx)), y: Math.max(Math.min(0, CROP_SIZE - ih), Math.min(Math.max(0, CROP_SIZE - ih), ny)) }; };
    const onMove = (ev) => setCropOffset(clamp(start.ox + ev.clientX - start.mx, start.oy + ev.clientY - start.my, cropScale));
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };

  const handleCropTouchStart = (e) => {
    const t0 = e.touches[0];
    const start = { mx: t0.clientX, my: t0.clientY, ox: cropOffset.x, oy: cropOffset.y };
    const clamp = (nx, ny, sc) => { const img = cropImgRef.current; if (!img) return { x: nx, y: ny }; const iw = img.width * sc, ih = img.height * sc; return { x: Math.max(Math.min(0, CROP_SIZE - iw), Math.min(Math.max(0, CROP_SIZE - iw), nx)), y: Math.max(Math.min(0, CROP_SIZE - ih), Math.min(Math.max(0, CROP_SIZE - ih), ny)) }; };
    const onMove = (ev) => { const tc = ev.touches[0]; setCropOffset(clamp(start.ox + tc.clientX - start.mx, start.oy + tc.clientY - start.my, cropScale)); };
    const onEnd = () => { window.removeEventListener("touchmove", onMove); window.removeEventListener("touchend", onEnd); };
    window.addEventListener("touchmove", onMove, { passive: true }); window.addEventListener("touchend", onEnd);
  };

  const handleCropZoom = (e) => {
    const ns = parseFloat(e.target.value); const img = cropImgRef.current; if (!img) return;
    const iw = img.width * ns, ih = img.height * ns;
    setCropOffset(prev => ({ x: Math.max(Math.min(0, CROP_SIZE - iw), Math.min(Math.max(0, CROP_SIZE - iw), prev.x)), y: Math.max(Math.min(0, CROP_SIZE - ih), Math.min(Math.max(0, CROP_SIZE - ih), prev.y)) }));
    setCropScale(ns);
  };

  const confirmarCrop = () => {
    const img = cropImgRef.current; if (!img) return;
    const canvas = document.createElement("canvas"); canvas.width = CROP_SIZE; canvas.height = CROP_SIZE;
    canvas.getContext("2d").drawImage(img, cropOffset.x, cropOffset.y, img.width * cropScale, img.height * cropScale);
    canvas.toBlob((blob) => { const f = new File([blob], "foto_projeto.jpg", { type: "image/jpeg" }); setFotoFile(f); setFotoRemovida(false); setFotoPreview(URL.createObjectURL(blob)); setCropSrc(null); }, "image/jpeg", 0.92);
  };

  const [pavimentosEdit, setPavimentosEdit] = useState((entity.pavimentos || []).filter(p => !p?.tipo || p.tipo === "pavimento").map((p) => ({ 
    id: p?.id ?? null, 
    nome: typeof p === "string" ? p : (p?.name ?? p?.nome ?? ""), 
    tipo: p?.tipo ?? "pavimento",
    _tempId: Math.random() 
  })));
  const [locacoesEdit, setLocacoesEdit] = useState((entity.pavimentos || []).filter(p => p?.tipo === "locacao").map((p) => ({ 
    id: p?.id ?? null, 
    nome: typeof p === "string" ? p : (p?.name ?? p?.nome ?? ""), 
    tipo: "locacao",
    _tempId: Math.random() 
  })));
  const [eapBlocks, setEapBlocks] = useState(() => {
    const eaps = entity.eap || [];
    const blocks = {};
    eaps.forEach((e) => {
      const bloco = e?.bloco ?? "EAP Direto";
      if (!blocks[bloco]) blocks[bloco] = [];
      blocks[bloco].push({
        id: e?.id ?? null,
        nome: typeof e === "string" ? e : (e?.name ?? e?.nome ?? ""),
        orcamento_base: e?.orcamento_base ?? null,
        classificacao: e?.classificacao ?? "direto",
        bloco: bloco,
        _tempId: Math.random()
      });
    });
    return Object.keys(blocks).length > 0 ? blocks : { "EAP Direto": [] };
  });
  const [novoBlocoName, setNovoBlocoName] = useState("");
  const [novoPavimento, setNovoPavimento] = useState("");
  const [novaLocacao, setNovaLocacao] = useState("");
  const [novoEapItem, setNovoEapItem] = useState({ nome: "", bloco: "", classificacao: "direto" });
  const [dragSourceType, setDragSourceType] = useState(null);
  const [dragSourceIndex, setDragSourceIndex] = useState(null);
  const [dragSourceBloco, setDragSourceBloco] = useState(null);
  const dragPav = useDragSort(setPavimentosEdit);
  const dragLoc = useDragSort(setLocacoesEdit);

  const handleEapDragStart = (e, bloco, index) => {
    setDragSourceType("eap");
    setDragSourceBloco(bloco);
    setDragSourceIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleEapDropBloco = (e, targetBloco) => {
    e.preventDefault();
    if (dragSourceType === "eap" && dragSourceBloco && dragSourceIndex !== null) {
      const sourceItems = eapBlocks[dragSourceBloco];
      const item = sourceItems?.[dragSourceIndex];
      if (!item) return;
      
      setEapBlocks((prev) => {
        const next = { ...prev };
        next[dragSourceBloco] = next[dragSourceBloco].filter((_, i) => i !== dragSourceIndex);
        if (!next[targetBloco]) next[targetBloco] = [];
        next[targetBloco] = [...next[targetBloco], { ...item, bloco: targetBloco }];
        return next;
      });
      setDragSourceType(null);
      setDragSourceBloco(null);
      setDragSourceIndex(null);
    }
  };

  const addEapBlock = () => {
    if (!novoBlocoName.trim()) return;
    setEapBlocks((prev) => ({ ...prev, [novoBlocoName.trim()]: [] }));
    setNovoBlocoName("");
  };

  const removeEapBlock = (bloco) => {
    setEapBlocks((prev) => {
      const next = { ...prev };
      delete next[bloco];
      return next;
    });
  };

  const addEapItem = (bloco) => {
    if (!novoEapItem.nome.trim()) return;
    setEapBlocks((prev) => ({
      ...prev,
      [bloco]: [...(prev[bloco] || []), { id: null, nome: novoEapItem.nome.trim(), orcamento_base: null, classificacao: novoEapItem.classificacao, bloco, _tempId: Math.random() }]
    }));
    setNovoEapItem({ nome: "", bloco: "", classificacao: "direto" });
  };

  const removeEapItem = (bloco, tempId) => {
    setEapBlocks((prev) => ({
      ...prev,
      [bloco]: prev[bloco].filter((e) => e._tempId !== tempId)
    }));
  };

  const handleCrossDragStart = (e, index, type) => {
    setDragSourceType(type);
    setDragSourceIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleCrossDropPavimentos = (e) => {
    e.preventDefault();
    if (dragSourceType === "locacao" && dragSourceIndex !== null) {
      const item = locacoesEdit[dragSourceIndex];
      if (!item) return;
      setLocacoesEdit((prev) => prev.filter((_, i) => i !== dragSourceIndex));
      setPavimentosEdit((prev) => [...prev, { ...item, tipo: "pavimento" }]);
      setDragSourceType(null);
      setDragSourceIndex(null);
    }
  };

  const handleCrossDropLocacoes = (e) => {
    e.preventDefault();
    if (dragSourceType === "pavimento" && dragSourceIndex !== null) {
      const item = pavimentosEdit[dragSourceIndex];
      if (!item) return;
      setPavimentosEdit((prev) => prev.filter((_, i) => i !== dragSourceIndex));
      setLocacoesEdit((prev) => [...prev, { ...item, tipo: "locacao" }]);
      setDragSourceType(null);
      setDragSourceIndex(null);
    }
  };
  const DIAS = ["Domingo","Segunda","Terça","Quarta","Quinta","Sexta","Sábado"];
  const [avisos, setAvisos] = useState([]);
  const [novoAviso, setNovoAviso] = useState({ texto: "", dia_semana: "" });

  useEffect(() => { if (isSetor || !entity?.id) return; supabase.from("project_avisos").select("*").eq("project_id", entity.id).then(({ data }) => { if (data) setAvisos(data); }); }, [isSetor, entity?.id]);
  const addAviso = () => { if (!novoAviso.texto.trim()) return; setAvisos((prev) => [...prev, { _tempId: Math.random(), project_id: entity.id, texto: novoAviso.texto.trim(), dia_semana: novoAviso.dia_semana !== "" ? parseInt(novoAviso.dia_semana) : null }]); setNovoAviso({ texto: "", dia_semana: "" }); };
  const removeAviso = (idx) => setAvisos((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    (async () => {
      const cid = containerId || entity?.container_id || entity?.user_id; if (!cid) return;
      const idsSet = new Set([cid]);
      const { data: convites } = await supabase.from("convites").select("user_id").eq("container_id", cid).eq("status", "aceito");
      (convites || []).forEach((c) => { if (c.user_id) idsSet.add(c.user_id); });
      const { data: perfis } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", [...idsSet]);
      if (perfis) setProfiles(perfis);
    })();
  }, [containerId, entity?.container_id]);

  useEffect(() => {
    if (!entity?.id) return;
    if (isSetor) {
      supabase.from("setor_members").select("user_id").eq("setor_id", entity.id).then(async ({ data: rows }) => {
        const ids = (rows || []).map((r) => r.user_id).filter(Boolean);
        if (!ids.length) { setMembrosSelecionados([]); return; }
        const { data: perfis } = await supabase.from("profiles").select("id, nickname, avatar_url").in("id", ids);
        setMembrosSelecionados(perfis || []);
      });
    } else {
      fetchMembrosDoProject(entity.id).then(setMembrosSelecionados);
    }
  }, [isSetor, entity?.id]);

  useEffect(() => {
    if (isSetor || !eap?.length) return;
    const ids = eap.map((e) => e?.id).filter(Boolean); if (!ids.length) return;
    supabase.from("eap").select("id, orcamento_base, bloco, classificacao").in("id", ids).then(({ data }) => {
      if (!data) return;
      const vals = {}; data.forEach((r) => { if (r.orcamento_base != null) vals[r.id] = String(r.orcamento_base).replace(".", ","); }); setEapValores(vals);
    });
  }, [isSetor, eap]);

  const totalEap = Object.values(eapBlocks).flat().reduce((acc, e) => { const key = e.id ?? e._tempId; const raw = eapValores[key] ?? (e.orcamento_base != null ? String(e.orcamento_base) : ""); const n = parseFloat(String(raw).replace(/\./g, "").replace(",", ".")); return acc + (isNaN(n) ? 0 : n); }, 0);
  const inccInicio = encontrarIndiceParaData(inccHistorico, form.data_inicio);
  const inccAtual = inccHistorico?.length ? inccHistorico[inccHistorico.length - 1] : null;
  const fatorIncc = inccInicio && inccAtual ? inccAtual.indice / inccInicio.indice : null;
  const totalCorrigido = fatorIncc && totalEap > 0 ? totalEap * fatorIncc : null;
  const variacaoIncc = fatorIncc ? ((fatorIncc - 1) * 100).toFixed(2) : null;
  const toggleMembro = (p) => setMembrosSelecionados((prev) => prev.find((m) => m.id === p.id) ? prev.filter((m) => m.id !== p.id) : [...prev, p]);

  // Função para agrupar membros por letra
  const agruparPorLetra = (membros) => {
    const grupos = {};
    membros.forEach(membro => {
      const primeiraLetra = membro.nickname?.charAt(0).toUpperCase() || '#';
      if (!grupos[primeiraLetra]) {
        grupos[primeiraLetra] = [];
      }
      grupos[primeiraLetra].push(membro);
    });
    // Ordenar as letras
    const letrasOrdenadas = Object.keys(grupos).sort();
    const resultado = {};
    letrasOrdenadas.forEach(letra => {
      resultado[letra] = grupos[letra];
    });
    return resultado;
  };

  const handleSave = async () => {
    setSalvando(true); setErro("");
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (isSetor) {
        let novaFotoUrl = entity.photo_url ?? null;
        if (fotoFile) novaFotoUrl = await uploadFotoSetor(entity.id, fotoFile);
        else if (fotoRemovida && entity.photo_url) { await removerFotosSetor(entity.id); novaFotoUrl = null; }

        const nomeFinal = form.nome?.trim() || entity.name;
        const { error: errSetor } = await supabase.from("setores").update({ name: nomeFinal }).eq("id", entity.id);
        if (errSetor) throw new Error(errSetor.message);

        await saveMembrosSetor(entity.id, membrosSelecionados, user?.id);

        onSave({ ...entity, name: nomeFinal, photo_url: novaFotoUrl, _membrosAtualizados: membrosSelecionados });
        onClose();
        return;
      }

      let novaFotoUrl = entity.photo_url ?? null;
      if (fotoFile) novaFotoUrl = await uploadFotoProjeto(entity.id, fotoFile);
      else if (fotoRemovida && entity.photo_url) { await removerFotoProjeto(entity.photo_url); novaFotoUrl = null; }

      // ── Geocoding: calcula coords se endereço mudou ou ainda não tem lat/lon ──
      let coords = { latitude: entity.latitude ?? null, longitude: entity.longitude ?? null };
      const cepMudou = endereco.cep.replace("-", "") !== (entity.cep ?? "").replace("-", "");
      const numeroMudou = endereco.numero !== (entity.numero ?? "");
      if (endereco.endereco && (cepMudou || numeroMudou || !entity.latitude)) {
        coords = await geocodificarEndereco(endereco);
      }

      // ── Nome final (mesmo padrão do setor, com fallback para o atual) ──
      const nomeFinal = form.nome?.trim() || entity.name;

      const updateProject = { 
        name: nomeFinal,
        photo_url: novaFotoUrl, 
        data_inicio: form.data_inicio || null, 
        data_finalizacao: form.data_finalizacao || null, 
        engenheiro_id: form.engenheiro_id || null, 
        etapa_atual: form.etapa_atual || null,
        indice_incc_inicio: inccInicio?.indice ?? null, 
        indice_incc_atual: inccAtual?.indice ?? null, 
        orcamento_corrigido: totalCorrigido ?? null,
        cep: endereco.cep || null,
        endereco: endereco.endereco || null,
        numero: endereco.numero || null,
        complemento: endereco.complemento || null,
        bairro: endereco.bairro || null,
        cidade: endereco.cidade || null,
        uf: endereco.uf || null,
        latitude: coords.latitude,
        longitude: coords.longitude,
        // ── Razão social + CNPJ ──
        razao_social: dadosEmpresa.razao_social.trim() || null,
        cnpj: dadosEmpresa.cnpj.replace(/\D/g, "") || null,
      };
      const { error: errProj } = await supabase.from("projects").update(updateProject).eq("id", entity.id);
      if (errProj) throw new Error(errProj.message);
      await saveMembros(entity.id, membrosSelecionados, user?.id);
      const idsOrigPav = (entity.pavimentos || []).map((p) => p?.id).filter(Boolean);
      const idsAtualPav = [...pavimentosEdit, ...locacoesEdit].map((p) => p.id).filter(Boolean);
      const removPav = idsOrigPav.filter((id) => !idsAtualPav.includes(id));
      if (removPav.length) await supabase.from("pavimentos").delete().in("id", removPav);
      for (let i = 0; i < pavimentosEdit.length; i++) { const p = pavimentosEdit[i]; if (p.id) await supabase.from("pavimentos").update({ name: p.nome, ordem: i, tipo: "pavimento" }).eq("id", p.id); else await supabase.from("pavimentos").insert({ name: p.nome, project_id: entity.id, ordem: i, tipo: "pavimento" }); }
      for (let i = 0; i < locacoesEdit.length; i++) { const l = locacoesEdit[i]; if (l.id) await supabase.from("pavimentos").update({ name: l.nome, ordem: i, tipo: "locacao" }).eq("id", l.id); else await supabase.from("pavimentos").insert({ name: l.nome, project_id: entity.id, ordem: i, tipo: "locacao" }); }
      const idsOrigEap = (entity.eap || []).map((e) => e?.id).filter(Boolean);
      const allEapItems = Object.values(eapBlocks).flat();
      const idsAtualEap = allEapItems.map((e) => e.id).filter(Boolean);
      const removEap = idsOrigEap.filter((id) => !idsAtualEap.includes(id));
      if (removEap.length) await supabase.from("eap").delete().in("id", removEap);
      
      let eapOrder = 0;
      for (const [bloco, items] of Object.entries(eapBlocks)) {
        for (let i = 0; i < items.length; i++) {
          const e = items[i];
          const raw = eapValores[e.id ?? e._tempId] ?? "";
          const val = parseFloat(String(raw).replace(/\./g, "").replace(",", "."));
          const orcBase = isNaN(val) ? (e.orcamento_base ?? null) : val;
          if (e.id) {
            await supabase.from("eap").update({ name: e.nome, ordem: eapOrder, orcamento_base: orcBase, bloco: bloco, classificacao: e.classificacao }).eq("id", e.id);
          } else {
            await supabase.from("eap").insert({ name: e.nome, project_id: entity.id, ordem: eapOrder, orcamento_base: orcBase, bloco: bloco, classificacao: e.classificacao });
          }
          eapOrder++;
        }
      }
      await supabase.from("project_avisos").delete().eq("project_id", entity.id);
      const avisosParaInserir = avisos.filter((a) => a.texto?.trim()).map((a) => ({ project_id: entity.id, texto: a.texto.trim(), dia_semana: a.dia_semana ?? null }));
      if (avisosParaInserir.length) await supabase.from("project_avisos").insert(avisosParaInserir);
      onSave({ ...entity, ...updateProject, _membrosAtualizados: membrosSelecionados, pavimentos: [...pavimentosEdit, ...locacoesEdit].map((p) => ({ id: p.id, name: p.nome, tipo: p.tipo })), eap: Object.values(eapBlocks).flat().map((e) => ({ id: e.id, name: e.nome, bloco: e.bloco, classificacao: e.classificacao })) });
      onClose();
    } catch (err) { setErro(err?.message || "Erro ao salvar."); }
    finally { setSalvando(false); }
  };

  return (
    <div className="ed-modal-overlay">
      <div className="ed-modal" style={{ maxWidth: "1020px", maxHeight: "85vh" }}>
        <div className="ed-modal-header"><h3>{isSetor ? "Editar Setor" : "Editar Projeto"}</h3><button type="button" className="ed-modal-close" onClick={onClose}><FaTimes /></button></div>
        
        <div style={{ display: "flex", borderBottom: "1px solid #e4e8f0", background: "#fafbff" }}>
          {tabsDisponiveis.filter(tab => tab !== "configuracoes" || isDono).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: "12px 16px",
                border: "none",
                background: activeTab === tab ? "#fff" : "transparent",
                color: activeTab === tab ? "#020a52" : "#888",
                fontSize: "12px",
                fontWeight: activeTab === tab ? 700 : 500,
                cursor: "pointer",
                borderBottom: activeTab === tab ? "3px solid #bf9010" : "none",
                textTransform: "uppercase",
                letterSpacing: "0.4px",
                transition: "all 0.2s",
              }}
            >
              {tab === "principal" && "Principal"}
              {tab === "integrantes" && "Integrantes"}
              {tab === "pavimentos" && "Pavimentos"}
              {tab === "eap" && "EAP"}
              {tab === "configuracoes" && "Configurações"}
            </button>
          ))}
        </div>

        <div className="ed-modal-body" style={{ maxHeight: "calc(85vh - 180px)", overflowY: "auto" }}>
          {/* ABA PRINCIPAL */}
          {activeTab === "principal" && (
            <>
              <div className="ed-modal-section">
                <p className="ed-modal-section-title">{isSetor ? "Foto do Setor" : "Foto do Projeto"}</p>
                {cropSrc ? (
                  <div className="ed-crop-container">
                    <div className="ed-crop-viewport" style={{ width: CROP_SIZE, height: CROP_SIZE }} onMouseDown={handleCropMouseDown} onTouchStart={handleCropTouchStart}>
                      <div className="ed-crop-grid" />
                      <img src={cropSrc} alt="" draggable={false} style={{ position: "absolute", left: cropOffset.x, top: cropOffset.y, width: cropImgRef.current ? cropImgRef.current.width * cropScale : "auto", height: cropImgRef.current ? cropImgRef.current.height * cropScale : "auto", userSelect: "none", pointerEvents: "none" }} />
                      <div className="ed-crop-mask" />
                    </div>
                    <div className="ed-crop-zoom-row">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      <input type="range" className="ed-crop-slider" min={Math.max(0.2, CROP_SIZE / Math.max(cropImgRef.current?.width || 1, cropImgRef.current?.height || 1))} max={4} step={0.01} value={cropScale} onChange={handleCropZoom} />
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                    </div>
                    <div className="ed-crop-actions">
                      <button type="button" className="ed-modal-foto-btn ed-modal-foto-btn--danger" onClick={() => { setCropSrc(null); if (fotoInputRef.current) fotoInputRef.current.value = ""; }}>Cancelar</button>
                      <button type="button" className="ed-modal-btn-save" style={{ padding: "7px 20px", fontSize: "12px" }} onClick={confirmarCrop}>Confirmar recorte</button>
                    </div>
                  </div>
                ) : (
                  <div className="ed-modal-foto-wrap">
                    <div className="ed-modal-foto-preview" onClick={() => fotoInputRef.current?.click()}>
                      {fotoPreview ? <img src={fotoPreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span className="ed-modal-foto-placeholder"><svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="4"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg><span>Sem foto</span></span>}
                      <div className="ed-modal-foto-overlay"><span>Alterar</span></div>
                    </div>
                    <div className="ed-modal-foto-actions">
                      <button type="button" className="ed-modal-foto-btn" onClick={() => fotoInputRef.current?.click()}>{fotoPreview ? "Trocar foto" : "Adicionar foto"}</button>
                      {fotoPreview && <button type="button" className="ed-modal-foto-btn ed-modal-foto-btn--danger" onClick={handleRemoverFoto}>Remover foto</button>}
                      <p className="ed-modal-foto-hint">JPG, PNG ou WebP · recomendado 400×400 px</p>
                    </div>
                  </div>
                )}
                <input ref={fotoInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={handleFotoChange} />
              </div>

              {isSetor && (
                <div className="ed-modal-section">
                  <p className="ed-modal-section-title">Nome do Setor</p>
                  <input
                    type="text"
                    className="ed-modal-select"
                    style={{ width: "100%", padding: "8px" }}
                    value={form.nome}
                    onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                    placeholder="Nome do setor"
                  />
                </div>
              )}

              {!isSetor && (
                <>
                  {/* ── Nome do Projeto ── */}
                  <div className="ed-modal-section">
                    <p className="ed-modal-section-title">Nome do Projeto</p>
                    <input
                      type="text"
                      className="ed-modal-select"
                      style={INPUT_STYLE}
                      value={form.nome}
                      onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                      placeholder="Nome do projeto"
                    />
                  </div>

                  {/* ── Dados da Obra (razão social + CNPJ) ── */}
                  <div className="ed-modal-section">
                    <p className="ed-modal-section-title">Dados da Obra</p>
                    <div className="ed-modal-row">
                      <div className="ed-modal-field" style={{ flex: 2 }}>
                        <label>Razão Social</label>
                        <input
                          type="text"
                          className="ed-modal-select"
                          style={INPUT_STYLE}
                          value={dadosEmpresa.razao_social}
                          onChange={(e) => setDadosEmpresa((p) => ({ ...p, razao_social: e.target.value }))}
                          placeholder="Ex: Construtora Silva Ltda"
                        />
                      </div>
                      <div className="ed-modal-field" style={{ maxWidth: 200 }}>
                        <label>CNPJ</label>
                        <input
                          type="text"
                          className="ed-modal-select"
                          style={INPUT_STYLE}
                          inputMode="numeric"
                          value={dadosEmpresa.cnpj}
                          onChange={(e) => setDadosEmpresa((p) => ({ ...p, cnpj: mascararCnpj(e.target.value) }))}
                          placeholder="00.000.000/0000-00"
                          maxLength={18}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="ed-modal-section">
                    <p className="ed-modal-section-title">Período da Obra</p>
                    <div className="ed-modal-row">
                      <div className="ed-modal-field"><label>Data de Início</label><input type="date" value={form.data_inicio} onChange={(e) => setForm((f) => ({ ...f, data_inicio: e.target.value }))} /></div>
                      <div className="ed-modal-field"><label>Data de Término</label><input type="date" value={form.data_finalizacao} onChange={(e) => setForm((f) => ({ ...f, data_finalizacao: e.target.value }))} /></div>
                    </div>
                  </div>

                  {/* ── ENDEREÇO DA OBRA ── */}
                  <div className="ed-modal-section">
                    <p className="ed-modal-section-title">Endereço da Obra</p>
                    <div className="ed-modal-row">
                      <div className="ed-modal-field" style={{ maxWidth: 160 }}>
                        <label>CEP</label>
                        <input type="text" className="ed-modal-select" style={INPUT_STYLE} value={endereco.cep} onChange={handleCepChange} placeholder="00000-000" maxLength={9} />
                        {buscandoCep && <span style={{ fontSize: 11, color: "#888" }}>Buscando...</span>}
                        {erroCep && <span style={{ fontSize: 11, color: "#c0392b" }}>{erroCep}</span>}
                      </div>
                    </div>
                    <div className="ed-modal-row">
                      <div className="ed-modal-field" style={{ flex: 2 }}>
                        <label>Logradouro</label>
                        <input type="text" className="ed-modal-select" style={INPUT_STYLE} value={endereco.endereco} onChange={(e) => setEndereco((p) => ({ ...p, endereco: e.target.value }))} placeholder="Rua, Avenida..." />
                      </div>
                      <div className="ed-modal-field" style={{ maxWidth: 100 }}>
                        <label>Número</label>
                        <input type="text" className="ed-modal-select" style={INPUT_STYLE} value={endereco.numero} onChange={(e) => setEndereco((p) => ({ ...p, numero: e.target.value }))} placeholder="123" />
                      </div>
                    </div>
                    <div className="ed-modal-row">
                      <div className="ed-modal-field">
                        <label>Complemento</label>
                        <input type="text" className="ed-modal-select" style={INPUT_STYLE} value={endereco.complemento} onChange={(e) => setEndereco((p) => ({ ...p, complemento: e.target.value }))} placeholder="Bloco, apto..." />
                      </div>
                      <div className="ed-modal-field">
                        <label>Bairro</label>
                        <input type="text" className="ed-modal-select" style={INPUT_STYLE} value={endereco.bairro} onChange={(e) => setEndereco((p) => ({ ...p, bairro: e.target.value }))} />
                      </div>
                    </div>
                    <div className="ed-modal-row">
                      <div className="ed-modal-field" style={{ flex: 2 }}>
                        <label>Cidade</label>
                        <input type="text" className="ed-modal-select" style={INPUT_STYLE} value={endereco.cidade} onChange={(e) => setEndereco((p) => ({ ...p, cidade: e.target.value }))} />
                      </div>
                      <div className="ed-modal-field" style={{ maxWidth: 80 }}>
                        <label>UF</label>
                        <input type="text" className="ed-modal-select" style={INPUT_STYLE} value={endereco.uf} maxLength={2} onChange={(e) => setEndereco((p) => ({ ...p, uf: e.target.value.toUpperCase() }))} />
                      </div>
                    </div>
                  </div>

                  <div className="ed-modal-section">
                    <p className="ed-modal-section-title">Etapa do Projeto</p>
                    <p className="ed-modal-avisos-hint">Em qual etapa a obra está atualmente? Isso aparece no card do container.</p>
                    <select
                      value={form.etapa_atual}
                      onChange={(e) => setForm((f) => ({ ...f, etapa_atual: e.target.value }))}
                      className="ed-modal-select"
                    >
                      <option value="">Selecione...</option>
                      {ETAPAS_PROJETO.map((etapa) => (
                        <option key={etapa} value={etapa}>{etapa}</option>
                      ))}
                    </select>
                  </div>

                  <div className="ed-modal-section">
                    <p className="ed-modal-section-title">Engenheiro Responsável</p>
                    <select value={form.engenheiro_id} onChange={(e) => setForm((f) => ({ ...f, engenheiro_id: e.target.value }))} className="ed-modal-select">
                      <option value="">Selecione...</option>
                      {profiles.map((p) => <option key={p.id} value={p.id}>{p.nickname}</option>)}
                    </select>
                  </div>

                  <div className="ed-modal-section">
                    <p className="ed-modal-section-title">Avisos na Listagem</p>
                    <p className="ed-modal-avisos-hint">Aparecem para todos os membros ao abrir a listagem fora do dia configurado.</p>
                    <div className="ed-modal-list-edit">
                      {avisos.map((a, i) => (<div key={a.id ?? a._tempId} className="ed-modal-aviso-item"><select className="ed-modal-aviso-dia" value={a.dia_semana ?? ""} onChange={(ev) => setAvisos((prev) => { const n=[...prev]; n[i]={...n[i], dia_semana: ev.target.value !== "" ? parseInt(ev.target.value) : null}; return n; })}><option value="">Sempre</option>{DIAS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}</select><input className="ed-modal-list-input" value={a.texto} onChange={(ev) => setAvisos((prev) => { const n=[...prev]; n[i]={...n[i], texto: ev.target.value}; return n; })} placeholder="Texto do aviso..." /><button type="button" className="ed-modal-list-del" onClick={() => removeAviso(i)}>×</button></div>))}
                      <div className="ed-modal-aviso-add"><select className="ed-modal-aviso-dia" value={novoAviso.dia_semana} onChange={(e) => setNovoAviso((v) => ({ ...v, dia_semana: e.target.value }))}><option value="">Sempre</option>{DIAS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}</select><input className="ed-modal-list-input" placeholder="+ Novo aviso..." value={novoAviso.texto} onChange={(e) => setNovoAviso((v) => ({ ...v, texto: e.target.value }))} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAviso(); } }} /><button type="button" className="ed-modal-list-btn" onClick={addAviso}>Adicionar</button></div>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* ABA INTEGRANTES - COM LISTA VERTICAL E AGRUPAMENTO POR LETRA */}
          {activeTab === "integrantes" && (
            <div className="ed-modal-section">
              <p className="ed-modal-section-title">{isSetor ? "Membros do Setor" : "Membros do Projeto"}</p>
              <div className="ed-modal-membros-lista">
                {profiles.length > 0 ? (
                  (() => {
                    const membrosOrdenados = [...profiles].sort((a, b) => a.nickname.localeCompare(b.nickname));
                    const grupos = agruparPorLetra(membrosOrdenados);
                    return Object.entries(grupos).map(([letra, membros]) => (
                      <div key={letra} className="ed-membro-grupo">
                        <div className="ed-membro-grupo-letra">{letra}</div>
                        <div className="ed-membro-grupo-lista">
                          {membros.map((p) => {
                            const sel = membrosSelecionados.some((m) => m.id === p.id);
                            return (
                              <div 
                                key={p.id} 
                                className={`ed-membro-item ${sel ? "ed-membro-item--sel" : ""}`} 
                                onClick={() => toggleMembro(p)}
                              >
                                <div className="ed-membro-item-av">
                                  {p.avatar_url ? <img src={p.avatar_url} alt={p.nickname} /> : p.nickname?.charAt(0).toUpperCase()}
                                </div>
                                <span className="ed-membro-item-nome">{p.nickname}</span>
                                {sel && <span className="ed-membro-item-check">✓</span>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()
                ) : (
                  <div className="ed-membro-empty">Nenhum membro encontrado.</div>
                )}
              </div>
            </div>
          )}

          {/* ABA PAVIMENTOS (apenas projeto) */}
          {!isSetor && activeTab === "pavimentos" && (
            <div className="ed-pav-grid">
              <div className="ed-modal-section ed-pav-container">
                <div className="ed-pav-header">
                  <p className="ed-modal-section-title">Pavimentos (Áreas Construtivas)</p>
                  <button 
                    ref={importBtnRef} 
                    type="button" 
                    className="ed-modal-import-btn"
                    onClick={() => setShowImportPopover((v) => !v)}
                  >
                    <img src={dataTransferImage} alt="" className="ed-modal-import-icon" /> 
                    Importar
                  </button>
                </div>
                <p className="ed-pav-desc">
                  Térreo, subsolo, pavimentos e outros níveis construtivos.
                </p>
                
                <div className="ed-pav-lista-scroll">
                  <div 
                    className="ed-modal-list-edit" 
                    onDragOver={(e) => e.preventDefault()} 
                    onDrop={(e) => { 
                      e.stopPropagation(); 
                      handleCrossDropPavimentos(e); 
                    }}
                  >
                    {pavimentosEdit.map((p, i) => (
                      <div 
                        key={p._tempId} 
                        className="ed-modal-list-item" 
                        draggable 
                        onDragStart={(e) => { 
                          dragPav.handleDragStart(e, i); 
                          handleCrossDragStart(e, i, "pavimento"); 
                        }} 
                        onDragOver={dragPav.handleDragOver} 
                        onDrop={(e) => { 
                          e.stopPropagation(); 
                          dragPav.handleDrop(e, i); 
                        }} 
                        onDragEnd={dragPav.handleDragEnd}
                      >
                        <span className="ed-drag-handle"><FaGripVertical /></span>
                        <input 
                          className="ed-modal-list-input" 
                          value={p.nome} 
                          onChange={(ev) => setPavimentosEdit((prev) => { 
                            const n = [...prev]; 
                            n[i] = { ...n[i], nome: ev.target.value }; 
                            return n; 
                          })} 
                          placeholder="Nome do pavimento (ex: Térreo)" 
                        />
                        <button 
                          type="button" 
                          className="ed-modal-list-del" 
                          onClick={() => setPavimentosEdit((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    <div className="ed-modal-list-add ed-pav-add-item">
                      <input 
                        className="ed-modal-list-input" 
                        placeholder="+ Novo pavimento" 
                        value={novoPavimento} 
                        onChange={(e) => setNovoPavimento(e.target.value)} 
                        onKeyDown={(e) => { 
                          if (e.key === "Enter" && novoPavimento.trim()) { 
                            e.preventDefault(); 
                            setPavimentosEdit((prev) => [...prev, { 
                              id: null, 
                              nome: novoPavimento.trim(), 
                              tipo: "pavimento", 
                              _tempId: Math.random() 
                            }]); 
                            setNovoPavimento(""); 
                          } 
                        }} 
                      />
                      <button 
                        type="button" 
                        className="ed-modal-list-btn" 
                        onClick={() => { 
                          if (!novoPavimento.trim()) return; 
                          setPavimentosEdit((prev) => [...prev, { 
                            id: null, 
                            nome: novoPavimento.trim(), 
                            tipo: "pavimento", 
                            _tempId: Math.random() 
                          }]); 
                          setNovoPavimento(""); 
                        }}
                      >
                        Adicionar
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="ed-modal-section ed-pav-container">
                <p className="ed-modal-section-title" style={{ marginBottom: 0, flexShrink: 0 }}>
                  Locações Administrativos
                </p>
                <p className="ed-pav-desc">
                  Canteiro, obra, barracão e áreas de apoio.
                </p>
                
                <div className="ed-pav-lista-scroll">
                  <div 
                    className="ed-modal-list-edit" 
                    onDragOver={(e) => e.preventDefault()} 
                    onDrop={(e) => { 
                      e.stopPropagation(); 
                      handleCrossDropLocacoes(e); 
                    }}
                  >
                    {locacoesEdit.map((l, i) => (
                      <div 
                        key={l._tempId} 
                        className="ed-modal-list-item" 
                        draggable 
                        onDragStart={(e) => { 
                          dragLoc.handleDragStart(e, i); 
                          handleCrossDragStart(e, i, "locacao"); 
                        }} 
                        onDragOver={dragLoc.handleDragOver} 
                        onDrop={(e) => { 
                          e.stopPropagation(); 
                          dragLoc.handleDrop(e, i); 
                        }} 
                        onDragEnd={dragLoc.handleDragEnd}
                      >
                        <span className="ed-drag-handle"><FaGripVertical /></span>
                        <input 
                          className="ed-modal-list-input" 
                          value={l.nome} 
                          onChange={(ev) => setLocacoesEdit((prev) => { 
                            const n = [...prev]; 
                            n[i] = { ...n[i], nome: ev.target.value }; 
                            return n; 
                          })} 
                          placeholder="Nome da locação (ex: Canteiro)" 
                        />
                        <button 
                          type="button" 
                          className="ed-modal-list-del" 
                          onClick={() => setLocacoesEdit((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    
                    <div className="ed-modal-list-add ed-pav-add-item">
                      <input 
                        className="ed-modal-list-input" 
                        placeholder="+ Nova locação" 
                        value={novaLocacao} 
                        onChange={(e) => setNovaLocacao(e.target.value)} 
                        onKeyDown={(e) => { 
                          if (e.key === "Enter" && novaLocacao.trim()) { 
                            e.preventDefault(); 
                            setLocacoesEdit((prev) => [...prev, { 
                              id: null, 
                              nome: novaLocacao.trim(), 
                              tipo: "locacao", 
                              _tempId: Math.random() 
                            }]); 
                            setNovaLocacao(""); 
                          } 
                        }} 
                      />
                      <button 
                        type="button" 
                        className="ed-modal-list-btn" 
                        onClick={() => { 
                          if (!novaLocacao.trim()) return; 
                          setLocacoesEdit((prev) => [...prev, { 
                            id: null, 
                            nome: novaLocacao.trim(), 
                            tipo: "locacao", 
                            _tempId: Math.random() 
                          }]); 
                          setNovaLocacao(""); 
                        }}
                      >
                        Adicionar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ABA EAP (apenas projeto) */}
          {!isSetor && activeTab === "eap" && (
            <>
              <div className="ed-modal-section">
                <p className="ed-modal-section-title">Criar Bloco de EAP</p>
                <div className="ed-modal-list-add">
                  <input 
                    className="ed-modal-list-input" 
                    placeholder="Ex: EAP Fachada, EAP Instalações..." 
                    value={novoBlocoName} 
                    onChange={(e) => setNovoBlocoName(e.target.value)} 
                    onKeyDown={(e) => { 
                      if (e.key === "Enter") { 
                        e.preventDefault(); 
                        addEapBlock(); 
                      } 
                    }} 
                  />
                  <button type="button" className="ed-modal-list-btn" onClick={addEapBlock}>
                    Criar Bloco
                  </button>
                </div>
              </div>

              <div className="ed-eap-container">
                {Object.entries(eapBlocks).map(([bloco, items]) => (
                  <div key={bloco} className="ed-eap-bloco">
                    <div className="ed-eap-bloco-header">
                      <p className="ed-modal-section-title">{bloco}</p>
                      <button 
                        type="button" 
                        className="ed-modal-foto-btn ed-modal-foto-btn--danger ed-eap-bloco-remove"
                        onClick={() => removeEapBlock(bloco)}
                      >
                        ✕
                      </button>
                    </div>
                    
                    <div className="ed-eap-lista-scroll">
                      <div className="ed-modal-list-edit">
                        {items.map((e, index) => {
                          const key = e.id ?? e._tempId;
                          return (
                            <div 
                              key={e._tempId} 
                              className="ed-eap-item"
                              draggable 
                              onDragStart={(ev) => handleEapDragStart(ev, bloco, index)} 
                              onDragOver={(e) => e.preventDefault()} 
                              onDrop={(e) => { 
                                e.stopPropagation(); 
                                handleEapDropBloco(e, bloco); 
                              }}
                            >
                              <div className="ed-eap-item-top">
                                <span className="ed-eap-drag-handle">⋮⋮</span>
                                <input 
                                  className="ed-eap-input-nome" 
                                  value={e.nome} 
                                  onChange={(ev) => setEapBlocks((prev) => ({ 
                                    ...prev, 
                                    [bloco]: prev[bloco].map((i) => 
                                      i._tempId === e._tempId ? { ...i, nome: ev.target.value } : i
                                    ) 
                                  }))} 
                                  placeholder="Nome do item EAP" 
                                />
                                <button 
                                  type="button" 
                                  className="ed-modal-list-del ed-eap-remove-item"
                                  onClick={() => removeEapItem(bloco, e._tempId)}
                                >
                                  ×
                                </button>
                              </div>

                              <div className="ed-eap-item-bottom">
                                <select 
                                  className="ed-eap-select-tipo"
                                  value={e.classificacao} 
                                  onChange={(ev) => setEapBlocks((prev) => ({ 
                                    ...prev, 
                                    [bloco]: prev[bloco].map((i) => 
                                      i._tempId === e._tempId ? { ...i, classificacao: ev.target.value } : i
                                    ) 
                                  }))}
                                >
                                  <option value="direto">Direto</option>
                                  <option value="indireto">Indireto</option>
                                </select>

                                <div className="ed-eap-valor-wrap">
                                  <span className="ed-eap-valor-prefix">R$</span>
                                  <input 
                                    type="text" 
                                    className="ed-eap-valor-input"
                                    placeholder="0,00" 
                                    value={eapValores[key] ?? (e.orcamento_base != null ? String(e.orcamento_base).replace(".", ",") : "")} 
                                    onChange={(ev) => setEapValores((prev) => ({ 
                                      ...prev, 
                                      [key]: ev.target.value 
                                    }))} 
                                  />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        
                        <div className="ed-eap-add-item">
                          <input 
                            className="ed-modal-list-input" 
                            placeholder="+ Novo item EAP" 
                            value={novoEapItem.bloco === bloco ? novoEapItem.nome : ""} 
                            onChange={(e) => setNovoEapItem({ ...novoEapItem, bloco, nome: e.target.value })} 
                            onKeyDown={(e) => { 
                              if (e.key === "Enter" && novoEapItem.bloco === bloco) { 
                                e.preventDefault(); 
                                addEapItem(bloco); 
                              } 
                            }} 
                          />
                          <select 
                            className="ed-eap-select-tipo-add"
                            value={novoEapItem.bloco === bloco ? novoEapItem.classificacao : "direto"} 
                            onChange={(e) => setNovoEapItem({ ...novoEapItem, bloco, classificacao: e.target.value })}
                          >
                            <option value="direto">Direto</option>
                            <option value="indireto">Indireto</option>
                          </select>
                          <button 
                            type="button" 
                            className="ed-modal-list-btn"
                            onClick={() => { 
                              novoEapItem.bloco = bloco; 
                              addEapItem(bloco); 
                            }}
                          >
                            Adicionar
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ABA CONFIGURAÇÕES */}
          {!isSetor && activeTab === "configuracoes" && isDono && (
            <ConfiguracoesProjeto
              entity={entity}
              entityType={entityType}
              usuarioId={usuarioLogadoId}
            />
          )}

          {erro && <p className="ed-modal-erro">{erro}</p>}
        </div>

        <div className="ed-modal-footer">
          <button type="button" className="ed-modal-btn-cancel" onClick={onClose}>Cancelar</button>
          <button type="button" className="ed-modal-btn-save" onClick={handleSave} disabled={salvando}><FaSave /> {salvando ? "Salvando..." : "Salvar"}</button>
        </div>
      </div>

      {!isSetor && showImportPopover && (
        <ImportarPopover containerId={containerId || entity?.container_id} currentProjectId={entity.id} anchorRef={importBtnRef}
          onImport={(imp) => { 
            if (imp.pavimentos?.length) {
              const pavs = imp.pavimentos.filter(p => !p.tipo || p.tipo === "pavimento");
              const locs = imp.pavimentos.filter(p => p.tipo === "locacao");
              if (pavs.length) setPavimentosEdit((p) => [...p, ...pavs]);
              if (locs.length) setLocacoesEdit((l) => [...l, ...locs]);
            }
            if (imp.eap?.length) {
              const newEaps = {};
              imp.eap.forEach((e) => {
                const bloco = e.bloco || "EAP Direto";
                if (!newEaps[bloco]) newEaps[bloco] = [];
                newEaps[bloco].push({ ...e, _tempId: Math.random() });
              });
              setEapBlocks((prev) => ({ ...prev, ...newEaps }));
            }
          }}
          onClose={() => setShowImportPopover(false)} />
      )}
    </div>
  );
}